import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { BadRequest } from 'shared/errors/bad-request'
import useUser from 'shared/hooks/use-user'
import { login } from 'modules/authentication/api/login-api'
import { LoginFormInterface } from 'modules/authentication/types/post-login-interface'

const hackFields = ['email', 'password'] as const

export const useLoginForm = ({ hasCustomDomain }: { hasCustomDomain?: boolean }) => {
  const router = useRouter()
  const { mutate } = useUser()
  const [isLoading, setIsLoading] = useState(false)

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<LoginFormInterface>()

  const formRef = useRef<HTMLFormElement>(null)

  // Need for autocomplete in firefox
  useEffect(() => {
    if (!formRef.current) return

    const formData = new FormData(formRef.current)
    hackFields.forEach(field => {
      const input = formData.get(field)
      if (input) {
        setValue(field as keyof LoginFormInterface, input as string)
      }
    })
  }, [])

  useEffect(() => {
    clearErrors()
  }, [router.locale])

  async function onSubmit(values: LoginFormInterface) {
    try {
      setIsLoading(true)
      const response = await login({ ...values })
      const responseUrl = (response.request as { responseURL?: string })?.responseURL
      const redirect = response.data?.redirect

      if (responseUrl?.includes('/2fa')) {
        router.push(responseUrl)
      } else {
        if (hasCustomDomain) {
          await mutate()
        }
        router.push(redirect, redirect, { locale: false })
      }
    } catch (e) {
      if (e instanceof BadRequest) {
        const { fields, common } = e.errors || {}

        if (fields) {
          Object.entries(fields).forEach(([key, messages]) => {
            if (key === 'email' || key === 'password') {
              setError(key as keyof LoginFormInterface, {
                message: messages?.join('\n'),
              })
            }
          })
        }

        if (common) {
          setError('root', { message: common?.join('\n') })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    formRef,
    handleSubmit,
    control,
    setValue,
    errors,
    isLoading,
    onSubmit,
    hasCustomDomain,
  }
}

export default useLoginForm
